import React, { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Loader2, ArrowLeft, ChevronRight, Bug } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { workoutService } from "../services/workout";
import { useActivityLogData } from "../hooks/useActivityLogData";
import { ActivityLogTable } from "../components/ActivityLogTable";
import { DeleteConfirmationModal } from "../components/DeleteConfirmationModal";
import { EditExerciseModal } from "../components/EditExerciseModal";

// Debug component to display raw data
function DebugPanel({ data }: { data: any }) {
  return (
    <Card className="mt-8 border-dashed border-red-500">
      <CardHeader className="bg-red-50 dark:bg-red-900/10">
        <div className="flex items-center">
          <Bug className="h-5 w-5 mr-2 text-red-500" />
          <CardTitle className="text-red-600">Debug Mode</CardTitle>
        </div>
        <CardDescription>
          Raw data from the activity log. Press 'd' to toggle debug mode.
        </CardDescription>
      </CardHeader>
      <CardContent className="overflow-auto max-h-[500px] bg-slate-50 dark:bg-slate-900/50">
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      </CardContent>
      <CardFooter className="bg-red-50 dark:bg-red-900/10 text-xs text-red-600">
        Debug mode is active. This information is only visible to you.
      </CardFooter>
    </Card>
  );
}

// Helper function to format time
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export default function ActivityLogPage() {
  const { isLoading, activityLog, error, navigate, id } = useActivityLogData();
  const [debugMode, setDebugMode] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // LOCAL STATE for editable activity log
  const [localActivityLog, setLocalActivityLog] = useState(activityLog);

  // Sync local state when activityLog changes
  useEffect(() => {
    setLocalActivityLog(activityLog);
  }, [activityLog]);

  // Edit Exercise Modal state
  const [editExerciseId, setEditExerciseId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Handle keyboard events for debug mode
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "d" || event.key === "D") {
        setDebugMode((prev) => !prev);
        if (!debugMode) {
          toast.info("Debug mode activated", {
            description: "Press 'd' again to deactivate",
            duration: 3000,
          });
        } else {
          toast.info("Debug mode deactivated");
        }
      }
    },
    [debugMode]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!localActivityLog) return <div>LOADING...</div>;

  // Find the exercise and result to edit
  const exerciseToEdit = editExerciseId ? localActivityLog.workout_snapshot?.exercises.find((ex: any) => ex.id === editExerciseId) : null;
  const resultToEdit = editExerciseId ? localActivityLog.workout_snapshot?.exercise_results[editExerciseId] : null;

  // Handler for editing an exercise by index (for ActivityLogTable)
  const handleEditExercise = (exerciseIdx: number) => {
    const exercises = localActivityLog?.workout_snapshot?.exercises || [];
    const ex = exercises[exerciseIdx];
    if (ex) {
      setEditExerciseId(ex.id);
      setEditModalOpen(true);
    }
  };

  const handleSaveEditExercise = async (updatedExercise: any, updatedResult: any) => {
    if (!updatedExercise || !localActivityLog?.workout_snapshot) return;
    const updatedExercises = localActivityLog.workout_snapshot.exercises.map((ex: any) =>
      ex.id === updatedExercise.id ? updatedExercise : ex
    );
    const updatedResults = {
      ...localActivityLog.workout_snapshot.exercise_results,
      [updatedExercise.id]: updatedResult,
    };
    const updatedSnapshot = {
      ...localActivityLog.workout_snapshot,
      exercises: updatedExercises,
      exercise_results: updatedResults,
    };
    // Update local state immediately for UI responsiveness
    setLocalActivityLog({
      ...localActivityLog,
      workout_snapshot: updatedSnapshot,
    });
    setEditModalOpen(false);
    // Persist changes to backend
    try {
      await workoutService.updateWorkoutLog(localActivityLog.id, { workoutSnapshot: updatedSnapshot });
      // Optionally show a toast for success
      toast.success('Exercise updated');
    } catch (err) {
      toast.error('Failed to update exercise');
      // Optionally revert local state or handle error
    }
  };

  // Handler for deleting workout log
  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await workoutService.deleteWorkoutLog(id);
      toast.success("Workout log deleted");
      navigate("/workout-history");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete workout log");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Handler for adding a new exercise
  const handleAddExercise = () => {
    if (!localActivityLog || !localActivityLog.workout_snapshot) return;
    const newExercise = {
      id: crypto.randomUUID(),
      name: '',
      type: 'sets',
      structure: { type: 'sets', sets: 3, reps: 10 },
      rest_time: 60,
      notes: '',
    };
    const newExercises = [...localActivityLog.workout_snapshot.exercises, newExercise];
    const updatedSnapshot = {
      ...localActivityLog.workout_snapshot,
      exercises: newExercises,
    };
    // Persist the new exercise
    workoutService.updateWorkoutLog(localActivityLog.id, { workoutSnapshot: updatedSnapshot })
      .then(() => {
        toast.success('Exercise added');
        window.location.reload();
      })
      .catch((error: any) => {
        toast.error(error?.message || 'Failed to add exercise');
      });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !activityLog) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Activity Log Not Found</h2>
        {error && <pre className="text-red-500 w-1/2">{error}</pre>}
        {!error && (
          <p className="text-muted-foreground mb-4">
            "The activity log you're looking for doesn't exist or you don't have
            permission to view it."
          </p>
        )}
        <Button onClick={() => navigate("/workout-history")}> 
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Workout History
        </Button>
      </div>
    );
  }

  // Get exercises and results from workout_snapshot if available
  const workoutSnapshot = localActivityLog.workout_snapshot;
  const exercises = workoutSnapshot?.exercises || [];
  const exerciseResults = workoutSnapshot?.exercise_results || {};

  // Extract workout name and date from the log
  const workoutName = localActivityLog.workout_name || "Workout";

  const workoutDate = new Date(localActivityLog.started_at);

  return (
    <div className="container mx-auto py-8 px-4 max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center mb-6"
      >
        <Button variant="ghost" onClick={() => navigate(-1)} className="mr-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{workoutName}</h1>
          <p className="text-muted-foreground flex items-center text-sm">
            {format(workoutDate, "MMMM d, yyyy 'at' h:mm a")}
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button
            variant={editMode ? "secondary" : "outline"}
            size="icon"
            onClick={() => setEditMode((v) => !v)}
            title={editMode ? "Exit Edit Mode" : "Edit Workout Snapshot"}
          >
          </Button>
          <Button
            variant="destructive"
            size="icon"
            onClick={() => setShowDeleteModal(true)}
            title="Delete Workout Log"
            disabled={isDeleting}
          >
          </Button>
        </div>
      </motion.div>



      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
      >
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="all">All Exercises</TabsTrigger>
            <TabsTrigger value="completed">Completed Only</TabsTrigger>
          </TabsList>
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <CardTitle>Exercise Details</CardTitle>
                <CardDescription>
                  All exercises performed during this workout
                </CardDescription>
              </CardHeader>
              <CardContent>
                {editMode && (
                  <div className="mb-4 flex justify-end">
                    <Button onClick={handleAddExercise} variant="outline">
                      + Add Exercise
                    </Button>
                </div>
                )}
                <ActivityLogTable
                  exercises={exercises}
                  exerciseResults={exerciseResults}
                  onEdit={handleEditExercise}
                />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="completed">
            <Card>
              <CardHeader>
                <CardTitle>Completed Exercises</CardTitle>
                <CardDescription>
                  Exercises you successfully completed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {exercises
                    .filter((ex: any) => {
                      const result = exerciseResults[ex.id];
                      return result?.completed;
                    })
                    .map((exercise: any) => {
                      const result = exerciseResults[exercise.id];
                      return (
                        <div
                          key={exercise.id}
                          className="p-4 rounded-lg border bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center">
                              <h3 className="font-medium">{exercise.name}</h3>
                            </div>
                            <span className="capitalize">{exercise.type}</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Sets:</span>{" "}
                              <span className="font-medium">{result?.sets_completed ?? 0} / {exercise.structure?.sets ?? 0}</span>
                            </div>
                            {exercise.structure?.reps && (
                              <div>
                                <span className="text-muted-foreground">Reps:</span>{" "}
                                <span className="font-medium">{exercise.structure.reps}</span>
                              </div>
                            )}
                            {exercise.structure?.duration && (
                              <div>
                                <span className="text-muted-foreground">Duration:</span>{" "}
                                <span className="font-medium">{exercise.structure.duration}s</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.6 }}
        className="mt-8 flex justify-center"
      >
        <Button onClick={() => navigate("/")} className="group" size="lg">
          Back to My Workouts
          <ChevronRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Button>
      </motion.div>

      <DeleteConfirmationModal open={showDeleteModal} onCancel={() => setShowDeleteModal(false)} onDelete={handleDelete} loading={isDeleting} />

   {  exerciseToEdit &&  <EditExerciseModal
        open={editModalOpen}
        exercise={exerciseToEdit}
        result={resultToEdit}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveEditExercise}
      />
}
      {/* Add debug panel at the end */}
      {debugMode && localActivityLog && <DebugPanel data={localActivityLog} />}
    </div>
  );
}
